import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { declineDriverRegistration } from "../../api/driver-registrations.api";
import "./decline-driver-registration.view.css";

type Status = "loading" | "success" | "error";

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return (
      (error.response?.data as { message?: string } | undefined)?.message ??
      error.message
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong.";
}

export function DeclineDriverRegistrationView() {
  const { driverRegistrationId, organizationId } = useParams<{
    driverRegistrationId: string;
    organizationId: string;
  }>();
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!driverRegistrationId || !organizationId) {
      setStatus("error");
      setError("Missing driver registration or organization id.");
      return;
    }

    setStatus("loading");
    setError("");
    declineDriverRegistration(driverRegistrationId, organizationId)
      .then(() => {
        setStatus("success");
      })
      .catch((error) => {
        setStatus("error");
        setError(getErrorMessage(error));
      });
  }, [driverRegistrationId, organizationId]);

  return (
    <div className="decline-driver-registration-view">
      {status === "loading" && (
        <div className="decline-loading" role="status" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          <span>Declining Driver Registration...</span>
        </div>
      )}
      {status === "success" && (
        <div className="decline-success">
          <p className="decline-success-title">
            Driver registration declined.
          </p>
          <p className="decline-success-note">
            This window can now be closed safely.
          </p>
        </div>
      )}
      {status === "error" && (
        <p className="decline-error" role="alert">
          Failed to decline driver registration: {error}
        </p>
      )}
    </div>
  );
}
